import { CreatePersonMember } from "../../generated/dto/CreatePersonMember";
import { UpdatePersonMember } from "../../generated/dto/UpdatePersonMember";
import { PersonMember } from "../../generated/dto/PersonMember";
import { extractResourceValidationErrors } from "../errors/ResourceValidationErrors";

export type PersonMemberAccessorFieldValues = Omit<UpdatePersonMember, "personId" | "allergiesDidSet" | "createdByDidSet" | "createdOnDidSet" | "dobDidSet" | "emailDidSet" | "employerDidSet" | "ethnicityDidSet" | "firstDidSet" | "genderDidSet" | "languageDidSet" | "lastDidSet" | "matchingGiftsDidSet" | "medicationsDidSet" | "middleDidSet" | "occupationDidSet" | "preferredDidSet" | "prefixDidSet" | "sailingExpDidSet" | "schoolDidSet" | "specialNeedsDidSet" | "suffixDidSet" | "swimProofDidSet" | "updatedByDidSet" | "updatedOnDidSet">;

/**
 * Backing store for `PersonMemberAccessor.fields` - a real getter/setter pair per editable
 * field (instead of a plain object) so a plain assignment, e.g.
 * `accessor.fields.allergies = value` (which is exactly what Vue's `v-model` does),
 * marks the field touched on its owning [PersonMemberAccessor] automatically - no separate
 * `accessor.set(field, value)` call needed.
 */
export class PersonMemberAccessorFields {
  private _allergies: string | null | undefined;
  private _createdBy: string | null | undefined;
  private _createdOn: Date | null | undefined;
  private _dob: Date | null | undefined;
  private _email: string | null | undefined;
  private _employer: string | null | undefined;
  private _ethnicity: string | null | undefined;
  private _first: string | null | undefined;
  private _gender: string | null | undefined;
  private _language: string | null | undefined;
  private _last: string | null | undefined;
  private _matchingGifts: string | null | undefined;
  private _medications: string | null | undefined;
  private _middle: string | null | undefined;
  private _occupation: string | null | undefined;
  private _preferred: string | null | undefined;
  private _prefix: string | null | undefined;
  private _sailingExp: string | null | undefined;
  private _school: string | null | undefined;
  private _specialNeeds: string | null | undefined;
  private _suffix: string | null | undefined;
  private _swimProof: number | null | undefined;
  private _updatedBy: string | null | undefined;
  private _updatedOn: Date | null | undefined;
  /** @internal - constructed only by PersonMemberAccessor. */
  constructor(private readonly owner: PersonMemberAccessor, initial: Partial<PersonMemberAccessorFieldValues>) {
    this._allergies = initial.allergies;
    this._createdBy = initial.createdBy;
    this._createdOn = initial.createdOn;
    this._dob = initial.dob;
    this._email = initial.email;
    this._employer = initial.employer;
    this._ethnicity = initial.ethnicity;
    this._first = initial.first;
    this._gender = initial.gender;
    this._language = initial.language;
    this._last = initial.last;
    this._matchingGifts = initial.matchingGifts;
    this._medications = initial.medications;
    this._middle = initial.middle;
    this._occupation = initial.occupation;
    this._preferred = initial.preferred;
    this._prefix = initial.prefix;
    this._sailingExp = initial.sailingExp;
    this._school = initial.school;
    this._specialNeeds = initial.specialNeeds;
    this._suffix = initial.suffix;
    this._swimProof = initial.swimProof;
    this._updatedBy = initial.updatedBy;
    this._updatedOn = initial.updatedOn;
  }

  get allergies(): string | null | undefined {
    return this._allergies;
  }

  set allergies(value: string | null | undefined) {
    this._allergies = value;
    this.owner.markTouched("allergies");
  }

  get createdBy(): string | null | undefined {
    return this._createdBy;
  }

  set createdBy(value: string | null | undefined) {
    this._createdBy = value;
    this.owner.markTouched("createdBy");
  }

  get createdOn(): Date | null | undefined {
    return this._createdOn;
  }

  set createdOn(value: Date | null | undefined) {
    this._createdOn = value;
    this.owner.markTouched("createdOn");
  }

  get dob(): Date | null | undefined {
    return this._dob;
  }

  set dob(value: Date | null | undefined) {
    this._dob = value;
    this.owner.markTouched("dob");
  }

  get email(): string | null | undefined {
    return this._email;
  }

  set email(value: string | null | undefined) {
    this._email = value;
    this.owner.markTouched("email");
  }

  get employer(): string | null | undefined {
    return this._employer;
  }

  set employer(value: string | null | undefined) {
    this._employer = value;
    this.owner.markTouched("employer");
  }

  get ethnicity(): string | null | undefined {
    return this._ethnicity;
  }

  set ethnicity(value: string | null | undefined) {
    this._ethnicity = value;
    this.owner.markTouched("ethnicity");
  }

  get first(): string | null | undefined {
    return this._first;
  }

  set first(value: string | null | undefined) {
    this._first = value;
    this.owner.markTouched("first");
  }

  get gender(): string | null | undefined {
    return this._gender;
  }

  set gender(value: string | null | undefined) {
    this._gender = value;
    this.owner.markTouched("gender");
  }

  get language(): string | null | undefined {
    return this._language;
  }

  set language(value: string | null | undefined) {
    this._language = value;
    this.owner.markTouched("language");
  }

  get last(): string | null | undefined {
    return this._last;
  }

  set last(value: string | null | undefined) {
    this._last = value;
    this.owner.markTouched("last");
  }

  get matchingGifts(): string | null | undefined {
    return this._matchingGifts;
  }

  set matchingGifts(value: string | null | undefined) {
    this._matchingGifts = value;
    this.owner.markTouched("matchingGifts");
  }

  get medications(): string | null | undefined {
    return this._medications;
  }

  set medications(value: string | null | undefined) {
    this._medications = value;
    this.owner.markTouched("medications");
  }

  get middle(): string | null | undefined {
    return this._middle;
  }

  set middle(value: string | null | undefined) {
    this._middle = value;
    this.owner.markTouched("middle");
  }

  get occupation(): string | null | undefined {
    return this._occupation;
  }

  set occupation(value: string | null | undefined) {
    this._occupation = value;
    this.owner.markTouched("occupation");
  }

  get preferred(): string | null | undefined {
    return this._preferred;
  }

  set preferred(value: string | null | undefined) {
    this._preferred = value;
    this.owner.markTouched("preferred");
  }

  get prefix(): string | null | undefined {
    return this._prefix;
  }

  set prefix(value: string | null | undefined) {
    this._prefix = value;
    this.owner.markTouched("prefix");
  }

  get sailingExp(): string | null | undefined {
    return this._sailingExp;
  }

  set sailingExp(value: string | null | undefined) {
    this._sailingExp = value;
    this.owner.markTouched("sailingExp");
  }

  get school(): string | null | undefined {
    return this._school;
  }

  set school(value: string | null | undefined) {
    this._school = value;
    this.owner.markTouched("school");
  }

  get specialNeeds(): string | null | undefined {
    return this._specialNeeds;
  }

  set specialNeeds(value: string | null | undefined) {
    this._specialNeeds = value;
    this.owner.markTouched("specialNeeds");
  }

  get suffix(): string | null | undefined {
    return this._suffix;
  }

  set suffix(value: string | null | undefined) {
    this._suffix = value;
    this.owner.markTouched("suffix");
  }

  get swimProof(): number | null | undefined {
    return this._swimProof;
  }

  set swimProof(value: number | null | undefined) {
    this._swimProof = value;
    this.owner.markTouched("swimProof");
  }

  get updatedBy(): string | null | undefined {
    return this._updatedBy;
  }

  set updatedBy(value: string | null | undefined) {
    this._updatedBy = value;
    this.owner.markTouched("updatedBy");
  }

  get updatedOn(): Date | null | undefined {
    return this._updatedOn;
  }

  set updatedOn(value: Date | null | undefined) {
    this._updatedOn = value;
    this.owner.markTouched("updatedOn");
  }

  /** @internal - used by PersonMemberAccessor to build the create/update payload. */
  toPlainObject(): Partial<PersonMemberAccessorFieldValues> {
    return {
      allergies: this._allergies,
      createdBy: this._createdBy,
      createdOn: this._createdOn,
      dob: this._dob,
      email: this._email,
      employer: this._employer,
      ethnicity: this._ethnicity,
      first: this._first,
      gender: this._gender,
      language: this._language,
      last: this._last,
      matchingGifts: this._matchingGifts,
      medications: this._medications,
      middle: this._middle,
      occupation: this._occupation,
      preferred: this._preferred,
      prefix: this._prefix,
      sailingExp: this._sailingExp,
      school: this._school,
      specialNeeds: this._specialNeeds,
      suffix: this._suffix,
      swimProof: this._swimProof,
      updatedBy: this._updatedBy,
      updatedOn: this._updatedOn,
    } as Partial<PersonMemberAccessorFieldValues>;
  }
}

/**
 * Generated frontend Accessor for `PersonMember` - wraps a draft of its editable
 * fields (`fields`, a [PersonMemberAccessorFields] with a real getter/setter per
 * field), tracks which ones were explicitly assigned, and turns that into the
 * `*DidSet` flags `UpdatePersonMember` requires automatically. `save()` picks create vs
 * update based on whether this accessor has an `id`, and on a 422 resource-validation
 * failure populates `fieldErrors` so a form can bind directly to
 * `accessor.fieldErrors.someField`.
 */
export class PersonMemberAccessor {
  readonly id: number | null;
  readonly fields: PersonMemberAccessorFields;
  fieldErrors: Record<string, string> = {};
  private readonly touched = new Set<keyof PersonMemberAccessorFieldValues>();

  private constructor(id: number | null, initial: Partial<PersonMemberAccessorFieldValues>) {
    this.id = id;
    this.fields = new PersonMemberAccessorFields(this, initial);
  }

  static forCreate(defaults: Partial<PersonMemberAccessorFieldValues> = {}): PersonMemberAccessor {
    return new PersonMemberAccessor(null, { allergies: defaults.allergies ?? null, createdBy: defaults.createdBy ?? null, createdOn: defaults.createdOn ?? null, dob: defaults.dob ?? null, email: defaults.email ?? null, employer: defaults.employer ?? null, ethnicity: defaults.ethnicity ?? null, first: defaults.first ?? null, gender: defaults.gender ?? null, language: defaults.language ?? null, last: defaults.last ?? null, matchingGifts: defaults.matchingGifts ?? null, medications: defaults.medications ?? null, middle: defaults.middle ?? null, occupation: defaults.occupation ?? null, preferred: defaults.preferred ?? null, prefix: defaults.prefix ?? null, sailingExp: defaults.sailingExp ?? null, school: defaults.school ?? null, specialNeeds: defaults.specialNeeds ?? null, suffix: defaults.suffix ?? null, swimProof: defaults.swimProof ?? null, updatedBy: defaults.updatedBy ?? null, updatedOn: defaults.updatedOn ?? null });
  }

  static forUpdate(existing: PersonMember): PersonMemberAccessor {
    return new PersonMemberAccessor(existing.personId, { allergies: existing.allergies, createdBy: existing.createdBy, createdOn: existing.createdOn, dob: existing.dob, email: existing.email, employer: existing.employer, ethnicity: existing.ethnicity, first: existing.first, gender: existing.gender, language: existing.language, last: existing.last, matchingGifts: existing.matchingGifts, medications: existing.medications, middle: existing.middle, occupation: existing.occupation, preferred: existing.preferred, prefix: existing.prefix, sailingExp: existing.sailingExp, school: existing.school, specialNeeds: existing.specialNeeds, suffix: existing.suffix, swimProof: existing.swimProof, updatedBy: existing.updatedBy, updatedOn: existing.updatedOn });
  }

  /** @internal - called by PersonMemberAccessorFields's setters; marks a field touched so
   * `save()` reports it as `DidSet` on update, and clears any stale field error. */
  markTouched(field: keyof PersonMemberAccessorFieldValues): void {
    this.touched.add(field);
    delete this.fieldErrors[field as string];
  }

  private buildCreate(): CreatePersonMember {
    return this.fields.toPlainObject() as CreatePersonMember;
  }

  private buildUpdate(): UpdatePersonMember {
    if (this.id == null) {
      throw new Error("PersonMemberAccessor.buildUpdate() called without an id - use forUpdate().");
    }
    return {
      ...this.fields.toPlainObject(),
      personId: this.id,
      allergiesDidSet: this.touched.has("allergies"),
      createdByDidSet: this.touched.has("createdBy"),
      createdOnDidSet: this.touched.has("createdOn"),
      dobDidSet: this.touched.has("dob"),
      emailDidSet: this.touched.has("email"),
      employerDidSet: this.touched.has("employer"),
      ethnicityDidSet: this.touched.has("ethnicity"),
      firstDidSet: this.touched.has("first"),
      genderDidSet: this.touched.has("gender"),
      languageDidSet: this.touched.has("language"),
      lastDidSet: this.touched.has("last"),
      matchingGiftsDidSet: this.touched.has("matchingGifts"),
      medicationsDidSet: this.touched.has("medications"),
      middleDidSet: this.touched.has("middle"),
      occupationDidSet: this.touched.has("occupation"),
      preferredDidSet: this.touched.has("preferred"),
      prefixDidSet: this.touched.has("prefix"),
      sailingExpDidSet: this.touched.has("sailingExp"),
      schoolDidSet: this.touched.has("school"),
      specialNeedsDidSet: this.touched.has("specialNeeds"),
      suffixDidSet: this.touched.has("suffix"),
      swimProofDidSet: this.touched.has("swimProof"),
      updatedByDidSet: this.touched.has("updatedBy"),
      updatedOnDidSet: this.touched.has("updatedOn"),
    } as UpdatePersonMember;
  }

  /**
   * Submits this draft: creates when `id` is null, updates otherwise. On a 422
   * resource-validation failure, populates `fieldErrors` and rethrows so the caller
   * can still decide how (or whether) to surface it beyond `fieldErrors`.
   */
  async save(actions: {
    create: (dto: CreatePersonMember) => Promise<PersonMember>;
    update: (dto: UpdatePersonMember) => Promise<PersonMember>;
  }): Promise<PersonMember> {
    try {
      const saved = this.id == null
        ? await actions.create(this.buildCreate())
        : await actions.update(this.buildUpdate());
      this.touched.clear();
      return saved;
    } catch (error) {
      const validationErrors = extractResourceValidationErrors(error);
      if (validationErrors) {
        validationErrors.forEach((validationError) => {
          if (validationError.field) {
            this.fieldErrors[validationError.field] = validationError.message;
          }
        });
      }
      throw error;
    }
  }
}
